import { useEffect } from 'react';
import { instanaReportError } from '@bizzabo/instana-api';

const CAPTIONHUB_PLUGIN_SCRIPT_URL = 'https://cdn-static.bizzabo.com/captionhub/mux-plugin-1.22.js';
const CAPTIONHUB_PLUGIN_SCRIPT_INTEGRITY = 'sha384-KOYIidC1feQF2dYNMg8SlbodKdMOtaCVNYd95u0nzdE3cTDyNP1S9smL/cRosVd5';
const MEDIA_READY_TIMEOUT_MS = 10000;
const CAPTION_TRACK_KINDS = ['subtitles', 'captions'];

let scriptPromise = null;

const loadCaptionHubScript = () => {
    if (window.CaptionHubLiveForMux) return Promise.resolve();
    if (scriptPromise) return scriptPromise;

    scriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = CAPTIONHUB_PLUGIN_SCRIPT_URL;
        script.integrity = CAPTIONHUB_PLUGIN_SCRIPT_INTEGRITY;
        script.crossOrigin = 'anonymous';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
            script.parentNode?.removeChild(script);
            // Reset so a subsequent call can retry instead of being stuck on the rejected promise.
            scriptPromise = null;
            reject(new Error('Failed to load CaptionHub script'));
        };
        document.body.appendChild(script);
    });

    return scriptPromise;
};

const pickPreferredShowing = (showingTracks) => {
    let prefLang = null;
    try {
        prefLang = window.localStorage?.getItem('media-chrome-pref-subtitles-lang');
    } catch (e) { /* ignore */ }
    const langs = (prefLang ? [prefLang] : []).concat(Array.from(window.navigator?.languages || []));
    const matches = (track) => track.language && langs.some(
        (lang) => track.language.toLowerCase().startsWith(lang.split('-')[0].toLowerCase())
    );
    return showingTracks.find(matches) || showingTracks[showingTracks.length - 1];
};

// CaptionHub's plugin sets mode="showing" on every track marked default in its JSON (both
// source and translations). MUX only collapses to a single showing track when its own
// auto-default flow is active (default-hidden-captions absent); when it's disabled, the
// browser stacks all "showing" subtitle tracks on top of each other. Enforce the
// single-showing invariant ourselves, unconditionally. Includes both 'subtitles' and
// 'captions' kinds so a stream-provided captions track and a CaptionHub subtitles track
// can't both render at once.
const enforceSingleShowing = (textTracks) => {
    const showing = Array.from(textTracks).filter(
        (t) => CAPTION_TRACK_KINDS.includes(t.kind) && t.mode === 'showing'
    );
    if (showing.length <= 1) return;
    const chosen = pickPreferredShowing(showing);
    showing.forEach((t) => {
        if (t !== chosen) t.mode = 'disabled';
    });
};

// Wait for <mux-player> to be inserted into the container by @bizzabo/broadcast (which
// only renders it once the stream goes live).
const waitForPlayer = (container, signal) => new Promise((resolve) => {
    if (!container) return resolve(null);

    const existing = container.querySelector('mux-player');
    if (existing) return resolve(existing);

    const observer = new MutationObserver(() => {
        const found = container.querySelector('mux-player');
        if (found) {
            observer.disconnect();
            resolve(found);
        }
    });
    signal.addEventListener('abort', () => {
        observer.disconnect();
        resolve(null);
    }, { once: true });
    observer.observe(container, { childList: true, subtree: true });
});

// Once <mux-player> is in the DOM, its internal <mux-video>/<video> chain is still being
// wired up. The plugin's preload calls `player.addTextTrack(...)`, which mux-player
// implements as a silent no-op until `this.media?.nativeEl` exists. On refresh the
// network round-trip loading the CaptionHub script masks this race; on SPA re-mount the
// script is cached and we race ahead of the player, so tracks (and the CC button) never
// appear. Poll until the native element is reachable.
const waitForMediaReady = (player, signal) => new Promise((resolve) => {
    const deadline = Date.now() + MEDIA_READY_TIMEOUT_MS;
    let rafId = null;

    signal.addEventListener('abort', () => {
        if (rafId !== null) cancelAnimationFrame(rafId);
        resolve(false);
    }, { once: true });

    const check = () => {
        if (signal.aborted) return;
        if (player.media?.nativeEl) return resolve(true);
        if (Date.now() > deadline) return resolve(false);
        rafId = requestAnimationFrame(check);
    };
    rafId = requestAnimationFrame(check);
});

// Tag the player with the URL we initialized it with so an effect re-run on the same
// player instance doesn't double-init and leave duplicate listeners/loops behind. On
// unmount/remount the player is a fresh DOM element with no tag, so this never blocks
// a legitimate re-init.
const initCaptionHubOnce = (player, captionsUrl) => {
    if (player._captionHubInitializedUrl === captionsUrl) return;
    window.CaptionHubLiveForMux.init({ video: player, captionsURL: captionsUrl });
    player._captionHubInitializedUrl = captionsUrl;
};

const useCaptionHub = ({ enabled, captionsUrl, containerRef }) => {
    useEffect(() => {
        if (!enabled || !captionsUrl) return undefined;

        const controller = new AbortController();
        const { signal } = controller;
        let textTracks = null;
        const onTracksChanged = () => textTracks && enforceSingleShowing(textTracks);

        const run = async() => {
            await loadCaptionHubScript();
            if (signal.aborted) return;

            await customElements.whenDefined('mux-player');
            if (signal.aborted) return;

            const player = await waitForPlayer(containerRef.current, signal);
            if (signal.aborted || !player || !window.CaptionHubLiveForMux) return;

            const ready = await waitForMediaReady(player, signal);
            if (signal.aborted || !ready) return;

            initCaptionHubOnce(player, captionsUrl);

            textTracks = player.textTracks;
            if (!textTracks) return;
            textTracks.addEventListener('addtrack', onTracksChanged);
            textTracks.addEventListener('change', onTracksChanged);
            onTracksChanged();
        };

        run().catch((err) => {
            if (signal.aborted) return;
            instanaReportError({ error: `[CaptionHub init]: ${err?.message ?? err}` });
        });

        return () => {
            controller.abort();
            if (textTracks) {
                textTracks.removeEventListener('addtrack', onTracksChanged);
                textTracks.removeEventListener('change', onTracksChanged);
            }
        };
    }, [enabled, captionsUrl, containerRef]);
};

export default useCaptionHub;
export { CAPTIONHUB_PLUGIN_SCRIPT_URL };
